import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { FlowsIcon, MoreIcon } from '../../shared/ui/icons';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from '../../shared/ui/menu';
import { isDefaultIn, isUsableSample, type TBookmark } from '../../entities/bookmark';
import { flowKeys, listFlows } from '../../entities/flow';

interface ISampleMenuProps {
  bookmark: TBookmark;
  editable: boolean;
  onUseIn: (flowId: string) => void;
  onRemove: () => void;
}

const Paragraph = ({ children }: { children: string }) => (
  <p className="px-inset py-label text-meta leading-17 text-muted">{children}</p>
);

/** Board 20's ⋯: only flows that start from the bookmark's source are offered. */
export const SampleMenu = ({ bookmark, editable, onUseIn, onRemove }: ISampleMenuProps) => {
  const [open, setOpen] = useState(false);
  const usable = isUsableSample(bookmark);
  const sourceId = bookmark.sourceId ?? '';
  const query = { startsFrom: sourceId, sort: 'name' as const, limit: 100 };
  const flows = useQuery({
    queryKey: flowKeys.list(query),
    queryFn: () => listFlows(query),
    enabled: open && usable,
  });
  const source = bookmark.sourceName ?? 'its source';
  const starting = flows.data?.data ?? [];

  return (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger
        aria-label={`More for ${bookmark.name}`}
        className="flex size-control-xs items-center justify-center rounded-item text-muted hover:bg-ghost hover:text-primary"
      >
        <MoreIcon size={14} />
      </MenuTrigger>
      <MenuContent align="end" className="w-menu-lg">
        <MenuLabel>Use as the test sample in</MenuLabel>
        {!usable && (
          <Paragraph>
            {bookmark.notUsableReason ?? 'Its source was deleted, so it cannot be a test sample.'}
          </Paragraph>
        )}
        {usable && flows.isPending && <Paragraph>Finding the flows that start from it…</Paragraph>}
        {usable && flows.isError && (
          <Paragraph>{`The flows could not be listed: ${flows.error.message}`}</Paragraph>
        )}
        {starting.map((flow) => {
          const current = isDefaultIn(bookmark, flow.id);
          return (
            <MenuItem
              key={flow.id}
              disabled={!editable || current}
              onSelect={() => onUseIn(flow.id)}
              selected={current}
            >
              <FlowsIcon size={13} />
              <span className="min-w-0 flex-1 break-words text-data text-primary">{flow.name}</span>
              {current && (
                <span className="shrink-0 font-mono text-micro text-faint">now default</span>
              )}
            </MenuItem>
          );
        })}
        {usable && flows.isSuccess && (
          <>
            <MenuSeparator />
            <Paragraph>
              {starting.length === 0
                ? `No flow starts from ${source} yet. Only those can use it — the others expect a different request.`
                : `Only flows that start from ${source} can use it — the others expect a different request.`}
            </Paragraph>
            {!editable && (
              <Paragraph>Only an owner or an admin can choose a flow’s test sample.</Paragraph>
            )}
          </>
        )}
        {editable && (
          <>
            <MenuSeparator />
            <MenuItem
              tone="danger"
              onSelect={onRemove}
              description="Its request stops being kept past retention"
            >
              Remove bookmark
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
};
